import { Patient } from "@models/patient";
import { FamilyMember } from "@models/family-member";
import { Residency } from "@models/residency";
import { Occupation } from "@models/occupation";
import {
  ImportantDateCategory,
  PatientImportantDate,
} from "@models/patient-important-date";
import { stringToDate } from "@utils/string.util";

export interface ResidencyFormType {
  id?: string;
  _id?: string;
  location?: string;
  startDate?: Date;
  endDate?: Date;
  delete?: boolean;
}

export interface OccupationFormType {
  id?: string;
  _id?: string;
  name?: string;
  startDate?: Date;
  endDate?: Date;
  delete?: boolean;
}

export interface ImportantDateFormType {
  id?: string;
  _id?: string;
  title?: string;
  date?: Date | null;
  category?: ImportantDateCategory;
  delete?: boolean;
}

export interface PersonFormType {
  id?: string;
  nickname?: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  birthDate?: Date | null;
  birthLocation?: string;
  bio?: string;
  hobbies?: string;
  personId?: string;
  gender?: string;
  occupations?: OccupationFormType[] | null;
  residencies?: ResidencyFormType[] | null;
}

export interface FamilyMemberFormType extends PersonFormType {
  _id?: string;
  relation?: string;
  relationTo?: string;
  delete?: boolean;
}

export interface PatientFormType extends PersonFormType {
  avatarUrl?: string;
  familyMembers?: FamilyMemberFormType[];
  language?: string;
  importantDates?: ImportantDateFormType[] | null;
}

export const residencyToFormType = (
  residency: Residency,
): ResidencyFormType => {
  return {
    ...residency,
    startDate: stringToDate(residency.startDate as string),
    endDate: stringToDate(residency.endDate as string),
  };
};

export const occupationToFormType = (
  occupation: Occupation,
): OccupationFormType => {
  return {
    ...occupation,
    startDate: stringToDate(occupation.startDate as string),
    endDate: stringToDate(occupation.endDate as string),
  };
};

export const familyMemberToFormType = (
  familyMember: FamilyMember,
): FamilyMemberFormType => {
  return {
    ...familyMember,
    birthDate: stringToDate(familyMember.birthDate as unknown as string),
    occupations: familyMember.occupations?.map(occupationToFormType),
    residencies: familyMember.residencies?.map(residencyToFormType),
  };
};

export const importantDateToFormType = (
  importantDate: PatientImportantDate,
): ImportantDateFormType => {
  return {
    id: importantDate.id,
    title: importantDate.title,
    date: new Date(2000, importantDate.month - 1, importantDate.day),
    category: importantDate.category ?? ImportantDateCategory.Other,
  };
};

export const patientToFormType = (patient: Patient): PatientFormType => {
  return {
    ...patient,
    birthDate: stringToDate(patient.birthDate as unknown as string),
    occupations: patient.occupations?.map(occupationToFormType),
    residencies: patient.residencies?.map(residencyToFormType),
    familyMembers: patient.familyMembers?.map(familyMemberToFormType),
    importantDates: patient.importantDates?.map(importantDateToFormType) ?? [],
  };
};
